import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, CheckCircle2, Loader2, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const RACES = [
  { value: 'mayor', label: 'Mayor' },
  { value: 'council', label: 'Ward Councillor' },
  { value: 'trustee', label: 'School Board Trustee' },
];
const WARDS = ['1', '2', '3', '4', '5', '6', '7', '8'];

// Weekly reminder sign-up. Single opt-in with an explicit consent line and a
// one-click unsubscribe in every email. No filters = everything in CKL.
export const ReminderSignup = ({ compact = false, testId = 'reminder-signup' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [races, setRaces] = useState([]);
  const [wards, setWards] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [preview, setPreview] = useState(null);
  const [started] = useState(() => Date.now());

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Please enter a valid email address.'); return; }
    if (!consent) { toast.error('Please tick the box to confirm you want the weekly email.'); return; }
    if (Date.now() - started < 2000) { toast.error('Please take a second to check the form, then try again.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/election/reminders/subscribe', { email: email.trim(), name: name.trim(), races, wards: races.includes('council') ? wards : [], consent, website: '' });
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not sign you up right now. Please try again.');
    } finally { setBusy(false); }
  };

  const showPreview = async () => {
    try {
      const r = await api.get('/election/reminders/preview', { params: { races: races.join(','), wards: races.includes('council') ? wards.join(',') : '' } });
      setPreview(r.data);
    } catch { toast.error('Preview is not available right now.'); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid={`${testId}-success`}>
        <p className="font-bold text-emerald-900 inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {done.message}</p>
        {!done.email_active && <p className="text-sm text-emerald-900/80 mt-1">Emails start as soon as the campaign mailbox is switched on. Your preferences are saved.</p>}
        <p className="text-xs text-emerald-900/70 mt-2">Every email has a one-click unsubscribe link. Your address is never shared or shown publicly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`rounded-2xl border border-[#c7d7f5] bg-[#f3f7ff] ${compact ? 'p-4' : 'p-5 sm:p-6'}`} data-testid={testId} noValidate>
      <p className="text-xs font-black uppercase tracking-widest text-[#24406f] inline-flex items-center gap-2"><BellRing className="h-4 w-4" /> Weekly reminder email</p>
      <p className="mt-1 font-bold text-[#1a2744] leading-snug">Get the week's debates and candidate events in your inbox every Monday morning.</p>
      <p className="text-sm text-slate-600 mt-0.5">Free. One email a week during the campaign. Unsubscribe any time with one click.</p>
      <div className={`mt-3 grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
        <div>
          <Label htmlFor={`${testId}-email`} className="text-xs font-semibold text-slate-600">Email *</Label>
          <Input id={`${testId}-email`} type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 mt-1 bg-white" data-testid={`${testId}-email`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-name`} className="text-xs font-semibold text-slate-600">First name (optional)</Label>
          <Input id={`${testId}-name`} value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1 bg-white" data-testid={`${testId}-name`} />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-xs font-semibold text-slate-600">Only send me events for (leave blank for everything in Kawartha Lakes):</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {RACES.map((r) => (
            <button key={r.value} type="button" onClick={() => toggle(races, setRaces, r.value)} aria-pressed={races.includes(r.value)} className={`h-9 px-3 rounded-lg border text-sm font-semibold ${races.includes(r.value) ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300'}`} data-testid={`${testId}-race-${r.value}`}>{r.label}</button>
          ))}
        </div>
        {races.includes('council') && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-wards`}>
            {WARDS.map((w) => (
              <button key={w} type="button" onClick={() => toggle(wards, setWards, w)} aria-pressed={wards.includes(w)} className={`h-9 min-w-[44px] px-2 rounded-lg border text-sm font-semibold ${wards.includes(w) ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300'}`} data-testid={`${testId}-ward-${w}`}>Ward {w}</button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-3 flex items-start gap-2">
        <Checkbox id={`${testId}-consent`} checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid={`${testId}-consent`} />
        <Label htmlFor={`${testId}-consent`} className="text-sm text-slate-700 leading-snug font-normal">Send me a weekly email listing upcoming debates and candidate events in Kawartha Lakes. I can unsubscribe with one click.</Label>
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy} className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid={`${testId}-submit`}>
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing up</> : 'Sign me up'}
        </Button>
        <button type="button" onClick={showPreview} className="inline-flex items-center text-sm font-semibold text-[#24406f] underline underline-offset-2" data-testid={`${testId}-preview-btn`}><Eye className="h-4 w-4 mr-1" /> See a sample email</button>
        <Link to="/elections/reminders" className="text-xs text-slate-500 underline underline-offset-2" data-testid={`${testId}-manage-link`}>Already signed up? Change or stop</Link>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid={`${testId}-preview-dialog`}>
          <DialogHeader><DialogTitle className="font-black text-[#1a2744] text-base">Sample: {preview?.subject}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Sent every Monday at 8:00 am Eastern. This is what it would contain right now{races.length ? ' with your filters' : ''}.</p>
          {preview && <iframe title="Sample weekly email" sandbox="" srcDoc={`<!doctype html><html><body style="margin:0;padding:8px;font-family:Arial,sans-serif;background:#f8fafc">${preview.html}</body></html>`} className="w-full flex-1 min-h-[420px] rounded-lg border border-slate-200 bg-white" data-testid={`${testId}-preview-frame`} />}
        </DialogContent>
      </Dialog>
    </form>
  );
};
